import { useLazyQuery } from '@apollo/client'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  Order as AtlasOrder,
  Collection,
  Door,
  OrderGeneralInfoDocument,
  OrderStatus,
  Warehouse,
} from '__generated__/atlas-types'

import { formatNameWithCode } from 'utils/formatters'
import { getOrderEditProductsLink } from 'utils/links'
import {
  fromGlobalId,
  relayConnectionToArray,
  toGlobalId,
} from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { useRenderToasters } from 'hooks/useRenderToasters'
import { getConnectedProvider } from 'payments/payments.transformations'
import {
  isEditApprovedShippedRestricted,
  isLiteRetailer as isLiteRetailerSelector,
  isProBrand as isProBrandSelector,
} from 'store/currentUser/selectors'
import { setPageLoaderActive } from 'store/pageLoader/actions'
import {
  applySelectedFilters,
  setDoors,
  setFilter,
  setGroupByTagInfo,
  setOrderPriceType,
  setRetailer,
  setSearchText,
  setSortByTagInfo,
  setWarehouse,
  toggleDeselectAllFilters,
} from 'store/shop/actions'
import { NONE_GROUP_BY_TAG, NONE_SORT_BY_TAG } from 'store/shop/constants'

import { PRODUCT_CATALOG } from 'routes/paths'

import { areOrderProductsEditable } from './components/Overview/components/ProductsWidget/utils'
import {
  useAccountPaymentProvidersQuery,
  useJoorPayUseReachFlag,
} from './review.queries'
import { COLLECTIONS_KEY } from 'containers/ProductCatalog/CatalogFilters/filters'
import { useOrderId } from 'features/Orders/orders.hooks'

type OrderGeneralFields = 'retailerProfile' | 'priceType'

type GeneralOrderInfoType = Pick<AtlasOrder, OrderGeneralFields> & {
  warehouse?: Warehouse
  collection: Collection
  door?: Door
}

export const useCleanupCatalogFilters = () => {
  const dispatch = useDispatch()

  const cleanupCatalogFilters = (): void => {
    dispatch(toggleDeselectAllFilters())
    dispatch(applySelectedFilters())
    dispatch(setSearchText(''))
    dispatch(setGroupByTagInfo(NONE_GROUP_BY_TAG))
    dispatch(setSortByTagInfo(NONE_SORT_BY_TAG))
  }

  return cleanupCatalogFilters
}

export const useSetupCatalogFilters = () => {
  const dispatch = useDispatch()

  const setupCatalogFilters = (order: GeneralOrderInfoType): void => {
    dispatch(
      setRetailer({
        ...order.retailerProfile,
        id: toGlobalId(
          'RetailerAccount',
          fromGlobalId(order?.retailerProfile?.id).id,
        ),
        displayName: order?.retailerProfile?.displayName,
      }),
    )
    dispatch(setOrderPriceType(order.priceType))
    dispatch(setWarehouse(order.warehouse))
    dispatch(
      setFilter({
        key: COLLECTIONS_KEY,
        value: {
          key: order.collection.id,
          value: order.collection.id,
          text: order.collection.name,
        },
      }),
    )
    if (order.door) {
      dispatch(
        setDoors([
          {
            key: order.door.id,
            value: order.door.id,
            text: formatNameWithCode(order.door.name, order.door.code),
          },
        ]),
      )
    }
  }

  return setupCatalogFilters
}

export const useRedirectToCatalog = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const cleanupCatalogFilters = useCleanupCatalogFilters()
  const setupCatalogFilters = useSetupCatalogFilters()
  const orderId = useOrderId()
  const { renderErrorToast } = useRenderToasters()
  const [getGeneralOrderInfoQuery] = useLazyQuery(OrderGeneralInfoDocument, {
    client: atlasClient,
    variables: {
      filters: {
        ids: [orderId],
      },
    },
  })

  const redirectToCatalog = async () => {
    try {
      dispatch(setPageLoaderActive(true))

      const response = await getGeneralOrderInfoQuery()

      if (response.error) {
        throw response.error
      }

      const order: AtlasOrder = relayConnectionToArray(
        response?.data?.orders,
      )?.[0]

      const formattedOrder: GeneralOrderInfoType = {
        retailerProfile: order.retailerProfile,
        warehouse: relayConnectionToArray(order.warehouses)?.[0],
        collection: relayConnectionToArray(order.collections)[0],
        door: relayConnectionToArray(order.doors)?.[0],
        priceType: order.priceType,
      }

      cleanupCatalogFilters()
      setupCatalogFilters(formattedOrder)

      navigate(PRODUCT_CATALOG)
    } catch (_) {
      renderErrorToast({
        description: 'Sorry, something went wrong. Try again later.',
      })
    } finally {
      dispatch(setPageLoaderActive(false))
    }
  }

  return redirectToCatalog
}

export const useJoorPay = (brandId?: string) => {
  const { joorPayUseReach } = useFlags()
  const isProBrand = useSelector(isProBrandSelector)
  const { data, loading: isAccountPaymentProvidersLoading } =
    useAccountPaymentProvidersQuery(brandId)
  const { data: joorPayUseReachQuery, loading: joorPayUseReachLoading } =
    useJoorPayUseReachFlag(brandId, isProBrand)
  const reachProvider = getConnectedProvider(
    data?.accountPaymentProviders || [],
    'reach',
  )

  return {
    isJoorPayEnabled: isProBrand
      ? Boolean(joorPayUseReach && reachProvider)
      : Boolean(joorPayUseReachQuery?.public?.useJoorPay && reachProvider),
    loading: joorPayUseReachLoading || isAccountPaymentProvidersLoading,
    joorPayUseReach: isProBrand
      ? joorPayUseReach
      : joorPayUseReachQuery?.public?.useJoorPay,
    reachProvider,
  }
}

/**
 * TODO: After we're in global release we should be able to remove this hook.
 * Also, we're not testing this on purpose as testing it would only end up
 * testing the same as what is tested in each `checkXConditions` function
 * and would just mock a bunch of things. Is okay if we test this manually,
 * specially considering is just one case (the redirect) and that we will
 * remove it later.
 *  */

export const useTabRedirections = ({
  webOrderLookupId,
  orderId: globalOrderId,
  orderStatus,
  orderInfoLoading,
}: UseTabRedirectionsParams) => {
  const isLiteRetailer = useSelector(isLiteRetailerSelector)
  const isEditApprovedShippedOrdersRestricted = useSelector(
    isEditApprovedShippedRestricted,
  )
  const canEditProducts = areOrderProductsEditable(
    orderStatus as OrderStatus | undefined,
    isLiteRetailer,
    isEditApprovedShippedOrdersRestricted,
  )
  const webOrderId = fromGlobalId(webOrderLookupId).id
  const orderId = fromGlobalId(globalOrderId)?.id

  return {
    overview: undefined,
    products: {
      url: orderInfoLoading
        ? undefined
        : getOrderEditProductsLink({
            order: {
              id: orderId || '',
              webOrderLookupId: webOrderId || '',
              status: orderStatus,
            },
            canEditProducts,
          }),
      loading: orderInfoLoading,
    },
    pay: {
      loading: orderInfoLoading,
      url: undefined,
    },
  }
}
type UseTabRedirectionsParams = {
  brandId?: string
  retailerId?: string
  webOrderLookupId?: string
  orderId?: string
  orderStatus?: NonNullable<OrderStatus>
  orderInfoLoading?: boolean
  isOrphanOrder?: boolean
}
